import { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router';
import { BotaoTema } from './BotaoTema';
import { LogoUCC } from './LogoUCC';
import { NotificationCenter } from './NotificationCenter';
import { SaldoNoCabecalho } from './MoedaUcc';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/cn';

/**
 * Classificação e artilharia não estão aqui de propósito: os dois são dados de
 * uma edição, e vivem nas abas do próprio campeonato. No topo eles obrigavam a
 * escolher a edição num seletor antes de mostrar qualquer coisa — um passo a
 * mais para chegar onde `/campeonatos` já leva.
 *
 * Rankings entra no topo pelo motivo oposto: abre somando todas as edições, sem
 * pedir escolha nenhuma, e é a página a que quem joga a UCC volta para ver onde
 * está.
 */
const NAV = [
  { to: '/', label: 'Início', end: true },
  { to: '/campeonatos', label: 'Campeonatos' },
  { to: '/rankings', label: 'Rankings' },
  { to: '/times', label: 'Times' },
  { to: '/loja', label: 'Loja' },
] as const;

export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { isStaff, user, loading } = useAuth();

  // O cabeçalho começa transparente sobre a hero e ganha fundo ao rolar —
  // sem isso, a barra corta a composição logo no primeiro pixel.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition-all duration-[--duration-standard] ease-[--ease-signature]',
        /*
          Sem `backdrop-blur` aqui, e essa é a maior causa do travamento na
          rolagem. Um cabeçalho fixo com desfoque de fundo obriga o navegador a
          reler e borrar tudo o que passa por trás dele a cada quadro — e como
          ele está fixo, isso acontece durante a rolagem inteira, em toda
          página. O engasgo aparece pior onde o fundo é mais complexo, que é
          exatamente a hero.

          Fundo quase opaco entrega a mesma leitura: o conteúdo não vaza através
          da barra, que era o que o desfoque protegia.
        */
        scrolled
          ? 'border-b border-line bg-ink/95'
          : 'border-b border-transparent bg-transparent',
      )}
    >
      <div className="mx-auto flex h-18 max-w-ucc items-center gap-4 px-4 sm:gap-8 sm:px-6">
        <Link
          to="/"
          className="flex items-center transition-opacity duration-[--duration-quick] hover:opacity-80"
          aria-label="UCC — página inicial"
        >
          <LogoUCC altura={38} />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Principal">
          {/* A central de comando entra na navegação principal para quem é da
              organização. Ela estava só no menu do avatar, e o organizador
              precisava lembrar de abrir um menu para chegar na tela que mais
              usa — quem administra a competição não deveria caçar o painel. */}
          {isStaff && (
            <NavLink
              to="/admin"
              end
              className={({ isActive }) =>
                cn(
                  'relative rounded-md px-3.5 py-2 text-sm font-semibold transition-colors duration-[--duration-quick]',
                  isActive ? 'text-gold-ink' : 'text-gold-ink/70 hover:text-gold-ink',
                  isActive &&
                    'after:absolute after:inset-x-3.5 after:-bottom-0.5 after:h-0.5 after:rounded-full after:bg-gold',
                )
              }
            >
              Painel
            </NavLink>
          )}
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={'end' in item ? item.end : false}
              className={({ isActive }) =>
                cn(
                  'relative rounded-md px-3.5 py-2 text-sm font-medium transition-colors duration-[--duration-quick]',
                  isActive ? 'text-paper' : 'text-muted hover:text-paper',
                  // Sublinhado dourado no item ativo: pinta a posição sem
                  // depender só de contraste de cinza.
                  isActive &&
                    'after:absolute after:inset-x-3.5 after:-bottom-0.5 after:h-0.5 after:rounded-full after:bg-gold',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-1.5 min-[360px]:gap-2.5">
          <BotaoTema className="hidden sm:inline-flex" />
          <SessionArea />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="menu-mobile"
            aria-label={open ? 'Fechar menu' : 'Abrir menu'}
            className="shrink-0 rounded-md p-2 text-muted transition-colors hover:text-paper lg:hidden"
          >
            <svg viewBox="0 0 20 20" className="size-5" fill="none" aria-hidden>
              <path
                d={open ? 'M5 5l10 10M15 5L5 15' : 'M3 6h14M3 10h14M3 14h14'}
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {open && (
        <nav
          id="menu-mobile"
          className="border-t border-line bg-ink/98 px-6 py-3 lg:hidden"
          aria-label="Principal (mobile)"
        >
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={'end' in item ? item.end : false}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                cn(
                  'block border-b border-line/60 py-3 text-sm font-medium last:border-0',
                  isActive ? 'text-gold-ink' : 'text-muted',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
          {/* Visitante: entrar e criar conta também no menu, com o texto que diz
              o que cada botão faz. */}
          {!loading && !user && (
            <div className="grid grid-cols-2 gap-2.5 border-t border-line/60 py-3">
              <Link
                to="/entrar"
                onClick={() => setOpen(false)}
                className="rounded-lg border border-line-strong px-4 py-3 text-center text-sm font-semibold transition-colors hover:border-gold/50"
              >
                Entrar
              </Link>
              <Link
                to="/cadastrar-time"
                onClick={() => setOpen(false)}
                className="rounded-lg bg-gold px-4 py-3 text-center text-sm font-semibold text-black transition-colors hover:bg-gold-bright"
              >
                Criar conta do time
              </Link>
            </div>
          )}
          <div className="flex items-center justify-between border-t border-line/60 pt-3 sm:hidden">
            <span className="text-sm font-medium text-muted">Tema</span>
            <BotaoTema />
          </div>
        </nav>
      )}
    </header>
  );
}

/**
 * Canto direito do cabeçalho.
 *
 * Visitante vê "Entrar" e o CTA de inscrição. Capitão logado vê o atalho para o
 * time dele — que é o lugar para onde ele volta toda vez. Quem é da organização
 * ganha também o link do painel, sem precisar decorar a URL.
 */
function SessionArea() {
  const { user, teams, isStaff, isAdmin, loading, logout } = useAuth();
  const [open, setOpen] = useState(false);

  if (loading) return <div className="h-10 w-32 animate-pulse rounded-lg bg-surface-2" aria-hidden />;

  if (!user) {
    return (
      <>
        {/* Entrar aparece também no celular: escondido ali, quem já tinha conta
            não achava por onde logar, e o menu não oferecia o caminho. */}
        <Link
          to="/entrar"
          className="inline-flex rounded-lg border border-line-strong px-3 py-2 text-sm min-[360px]:px-3.5 font-semibold whitespace-nowrap transition-colors duration-[--duration-quick] hover:border-gold/50 sm:px-5 sm:py-2.5"
        >
          Entrar
        </Link>
        <Link
          to="/cadastrar-time"
          className="rounded-lg bg-gold px-3 py-2 text-sm font-semibold whitespace-nowrap text-black transition-colors duration-[--duration-quick] hover:bg-gold-bright min-[360px]:px-3.5 sm:px-5 sm:py-2.5"
        >
          {/* Abaixo de 360px o "time" sai, para o menu ainda caber na linha. */}
          Inscrever<span className="hidden min-[360px]:inline"> time</span>
        </Link>
      </>
    );
  }

  const team = teams[0];

  return (
    <div className="flex items-center gap-2">
      <SaldoNoCabecalho />
      <NotificationCenter />

      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex items-center gap-2.5 rounded-lg border border-line-strong px-3 py-2 text-sm font-medium transition-colors hover:border-gold/50"
        >
        {user.avatarUrl ? (
          <img src={user.avatarUrl} alt="" className="size-6 rounded-full" />
        ) : (
          <span className="flex size-6 items-center justify-center rounded-full bg-gold/15 text-[0.625rem] font-bold text-gold-ink">
            {user.displayName.slice(0, 2).toUpperCase()}
          </span>
        )}
        <span className="hidden max-w-28 truncate sm:inline">{user.displayName}</span>
      </button>

      {open && (
        <>
          {/* Camada invisível que fecha o menu ao clicar fora, sem listener global. */}
          <button
            type="button"
            aria-label="Fechar menu"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 cursor-default"
          />
          <div className="ucc-panel absolute right-0 z-50 mt-2 w-56 overflow-hidden py-1 shadow-[var(--shadow-lift)]">
            <p className="truncate border-b border-line px-4 py-2.5 text-xs text-faint">
              {user.email ?? user.displayName}
            </p>

            {team ? (
              <Link
                to={`/meu-time/${team.id}`}
                onClick={() => setOpen(false)}
                className="block px-4 py-2.5 text-sm text-muted transition-colors hover:bg-surface-2 hover:text-paper"
              >
                {team.name}
              </Link>
            ) : (
              <Link
                to="/meu-time/novo"
                onClick={() => setOpen(false)}
                className="block px-4 py-2.5 text-sm text-gold-ink transition-colors hover:bg-surface-2"
              >
                Cadastrar meu time
              </Link>
            )}

            {/* Bloco da organização.
                Só aparece para quem tem papel `admin` ou `staff` — e não é só
                a tela que esconde: cada rota de `/api/admin/*` exige o papel no
                servidor. Esconder o link é conveniência; a permissão de verdade
                mora na API, onde ninguém contorna abrindo o endereço na mão. */}
            {isStaff && (
              <>
                <p className="border-t border-line px-4 pt-2.5 pb-1 text-[0.625rem] tracking-wider text-faint uppercase">
                  Organização
                </p>
                {[
                  { to: '/admin', label: 'Central de comando' },
                  { to: '/admin/campeonatos', label: 'Campeonatos' },
                  { to: '/admin/tickets', label: 'Tickets' },
                  { to: '/admin/moedas', label: 'Moedas & Loja' },
                  // Só admin: o coletor é infraestrutura da organização, e a
                  // listagem devolve o IP residencial de quem hospeda o agente.
                  ...(isAdmin
                    ? [
                        { to: '/admin/usuarios', label: 'Usuários & Permissões' },
                        { to: '/admin/coletor', label: 'Agentes do coletor' },
                      ]
                    : []),
                ].map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setOpen(false)}
                    className="block px-4 py-2 text-sm text-muted transition-colors hover:bg-surface-2 hover:text-gold-ink"
                  >
                    {item.label}
                  </Link>
                ))}
              </>
            )}

            <button
              type="button"
              onClick={() => {
                setOpen(false);
                logout();
              }}
              className="block w-full border-t border-line px-4 py-2.5 text-left text-sm text-muted transition-colors hover:bg-surface-2 hover:text-loss"
            >
              Sair
            </button>
          </div>
        </>
      )}
      </div>
    </div>
  );
}
